import React from 'react';
import {
  NOT,
  YES
} from '../../../lib/Boolean.lib';
import Core from '../../../lib/Core';
import {
  ID__ATS_TYPE__LEVER_API
} from '../../../lib/Definition';
import globalErrorHandler from '../../../lib/Error/globalErrorHandler.fun';
import {
  Fun
} from '../../../lib/Function.lib';
import useState from '../../../lib/hooks/useState.hook';
import {
  Obj
} from '../../../lib/Object.lib';
import getAtsContextProperty from '../../../lib/services/ATS/getAtsContextProperty.fun';
import getAtsContextUpdated from '../../../lib/services/ATS/getAtsContextUpdated.fun';
import Lever from '../../../lib/services/ATS/Lever.lib';
import {
  mapContactsToStrings
} from '../../../lib/services/Email/Email.lib';
import {
  compileText,
  joinKeyName,
  trim
} from '../../../lib/String.lib';
import {
  showConfirm
} from '../../Dialogs/AppConfirmationDialog';
import Box from '../../Layout/Wrappers/Box';
import Button from '../../Layout/Wrappers/Button';
import Fieldset from '../../Layout/Wrappers/Fieldset';
import Menu from '../../Layout/Wrappers/Menu';
import {
  ErrorMessage,
  LoadingMessage,
  SuccessChipMessage,
  WarningChipMessage
} from '../../Layout/Wrappers/Message';
import Once from '../../Layout/Wrappers/Once';
import Typography from '../../Layout/Wrappers/Typography';

export const EMPLOYER_SETTINGS__LABELS = {
  leverSection: {
    title: '10x10 Lever settings',
    subsection1: {
      title: 'Application authorization'
    },
    subsection2: {
      title: 'Connection settings'
    }
  },
  confirm: {
    title: 'Disconnect?',
    message: 'This action will remove all Lever credentials for {{EMPLOYER__NAME}}',
    onAccept: 'Proceed'
  },
  validateSource: 'Validate',
  isValidSource: 'Validated source!',
  invalidSource: 'Please select a 10x10 agency',
  invalidUser: 'Please select a user',
  verifyingConnection: 'Verifying connection state',
  loadingResources: 'Loading settings',
  connect: 'Connect',
  disconnect: 'Disconnect',
  connected: 'Connected',
  disconnected: 'Disconnected',
  validated: 'Validated',
  invalid: 'Invalid'
};

export default function SubSectionAtsSettingsLeverApi({
  acl = true,
  entity = {},
  onChange = Fun,
  ...props
}) {
  const mem = React.useRef({});
  // ON UNMOUNT
  React.useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      clearInterval(mem.current.interval);
    };
  }, []);
  const [state, updateState] = useState();
  if (NOT(acl)) { return null; }

  const {
    isConnected,
    error = false,
    busy = false,
    ready = false,
  } = state;

  const VALUE__EMPLOYER__ID = trim(entity.id);

  const YES__CONNECTED = YES(isConnected);

  return (
    <Box column flex1 {...props}>
      <Once
        acl={VALUE__EMPLOYER__ID}
        promise={async () => {
          try {
            await updateState({ busy: true });
            await updateState({
              isConnected: await Lever.isConnected({
                employerId: VALUE__EMPLOYER__ID
              }),
              busy: false,
              ready: true
            });
          }
          catch (error) {
            await updateState({ error });
          }
        }}
      />
      <Box row flex1>
        <LoadingMessage show={busy && !error}>
          {EMPLOYER_SETTINGS__LABELS.verifyingConnection}
        </LoadingMessage>
      </Box>
      {ready && (
        <Box column flex1>
          <Fieldset column
            title={EMPLOYER_SETTINGS__LABELS.leverSection.subsection1.title}
            className='d-flex flex-align-left-center'
            fullWidth={false}
          >
            <Box row w100>
              <span className='c-black-medium f-md mr-2'>Status:</span>
              <SuccessChipMessage show={YES__CONNECTED} className='mr-2'>
                {EMPLOYER_SETTINGS__LABELS.connected}
              </SuccessChipMessage>
              <WarningChipMessage show={NOT(YES__CONNECTED)} className='mr-2'>
                {EMPLOYER_SETTINGS__LABELS.disconnected}
              </WarningChipMessage>
            </Box>
            <Button small
              onClick={(event) => {
                if (YES__CONNECTED) {
                  showConfirm({
                    title: EMPLOYER_SETTINGS__LABELS.confirm.title,
                    message: compileText(EMPLOYER_SETTINGS__LABELS.confirm.message, {
                      EMPLOYER__NAME: entity.name
                    }),
                    onAcceptLabel: EMPLOYER_SETTINGS__LABELS.confirm.onAccept,
                    onAccept: () => {
                      Lever.disconnect({
                        employerId: VALUE__EMPLOYER__ID
                      }).then(
                        (response) => {
                          Core.showMessage(
                            response.message,
                            () => Lever.isConnected({
                              employerId: VALUE__EMPLOYER__ID
                            }).then(
                              (isConnected) => updateState({
                                isConnected
                              })
                            )
                          );
                        }
                      ).catch(Core.showError);
                    }
                  });
                }
                else {
                  Lever.connect({
                    employerId: VALUE__EMPLOYER__ID
                  });
                  mem.current.interval = setInterval(() => {
                    Lever.isConnected({
                      employerId: VALUE__EMPLOYER__ID
                    }).then(
                      (isConnected) => {
                        if (isConnected) {
                          updateState({ isConnected });
                          clearInterval(mem.current.interval);
                        }
                      }
                    );
                  }, 1000);
                }
              }}
              startIcon={
                <i className='material-icons c-inherit'>
                  {YES__CONNECTED ? 'logout' : 'logins'}
                </i>
              }
              variant={YES__CONNECTED ? 'outlined' : 'contained'}
              className='mt-2'
            >
              {YES__CONNECTED ? EMPLOYER_SETTINGS__LABELS.disconnect : EMPLOYER_SETTINGS__LABELS.connect}
            </Button>
          </Fieldset>
          {YES__CONNECTED && (
            <LeverSettingsConnected
              entity={entity}
              onChange={onChange}
            />
          )}
        </Box>
      )}
      <ErrorMessage show={error} className='m-2 mt-3'>
        {error}
      </ErrorMessage>
    </Box>
  );
}


function LeverSettingsConnected({
  entity = {},
  onChange = Fun
}) {
  const [state, updateState] = useState();
  const {
    sources = [],
    users = [],
    error = false,
    busy = false,
    ready = false,
    userLabel = ''
  } = state;
  const VALUE__EMPLOYER__ID = trim(entity.id);
  const VALUE__ATS__USER__ID = getAtsContextProperty({
    entity,
    atsTypeId: ID__ATS_TYPE__LEVER_API,
    property: 'userId'
  });
  const VALUE__ATS__AGENCY_NAME = getAtsContextProperty({
    entity,
    atsTypeId: ID__ATS_TYPE__LEVER_API,
    property: 'agencyName'
  });
  return (
    <Box column flex1>
      <Once
        promise={async () => {
          try {
            await updateState({ busy: true });
            const tenByTenUser = Core.getUser(); tenByTenUser.name = Core.getUserName();
            const leverUser = Obj(users.find(({ email }) => email === tenByTenUser.email));
            const leverSource = Obj(sources.find(({ text }) => Core.isTenByTenAlias(text)));
            const isValidUser = YES(leverUser.id);
            const isValidSource = YES(leverSource.text);
            const update = {};
            if (isValidUser && NOT(VALUE__ATS__USER__ID)) {
              update.userId = leverUser.id;
            }
            if (isValidSource && NOT(VALUE__ATS__AGENCY_NAME)) {
              update.agencyName = leverSource.text;
            }
            if (!!Object.keys(update).length) {
              onChange(
                getAtsContextUpdated({
                  entity,
                  atsTypeId: ID__ATS_TYPE__LEVER_API,
                  update
                })
              );
            }
            await updateState({
              userLabel: isValidUser ? mapContactsToStrings([leverUser]) : mapContactsToStrings([tenByTenUser]),
              users: await Lever.getUsers({
                employerId: VALUE__EMPLOYER__ID
              }),
              sources: await Lever.getSources({
                employerId: VALUE__EMPLOYER__ID
              }),
              busy: false,
              error: false,
              ready: true
            });
          }
          catch (error) {
            globalErrorHandler(error);
          }
        }}
        render={false}
      />
      <Box row w100>
        <LoadingMessage show={busy && !error} className='m-2 mt-3 w-100'>
          {EMPLOYER_SETTINGS__LABELS.loadingResources}
        </LoadingMessage>
        <ErrorMessage show={error} className='m-2 mt-3 w-100'>
          {error}
        </ErrorMessage>
      </Box>
      {ready && (
        <Fieldset
          title={EMPLOYER_SETTINGS__LABELS.leverSection.subsection2.title}
        >
          <Box column>
            <Box row>
              <Typography className='mr-2 min-w-80'>
                Source:
              </Typography>
              <Box row className='min-w-180 mr-2'>
                <Menu dropdown
                  name={joinKeyName([
                    'employer_settings',
                    VALUE__EMPLOYER__ID,
                    'sources'
                  ])}
                  value={VALUE__ATS__AGENCY_NAME}
                  onChange={async (agencyName) => {
                    onChange(
                      getAtsContextUpdated({
                        entity,
                        atsTypeId: ID__ATS_TYPE__LEVER_API,
                        update: {
                          agencyName
                        }
                      })
                    );
                  }}
                  options={sources.map(({ text }) => ({
                    id: text,
                    label: (
                      Core.isTenByTenAlias(text)
                        ? (
                          <div className='d-flex flex-align-left-center'>
                            <span className='mr-1'>{text}</span>
                            <i className='material-icons c-cyan icon16'>verified</i>
                          </div>
                        )
                        : text
                    )
                  }))}
                />
              </Box>
              <WarningChipMessage show={NOT(Core.isTenByTenAlias(VALUE__ATS__AGENCY_NAME))}>
                {EMPLOYER_SETTINGS__LABELS.invalidSource}
              </WarningChipMessage>
            </Box>
            <Box row className='mt-1'>
              <Typography className='mr-2 min-w-80'>
                User:
              </Typography>
              <Box row className='min-w-180 mr-2'>
                <Menu dropdown
                  name={joinKeyName([
                    'employer_settings',
                    VALUE__EMPLOYER__ID,
                    'users'
                  ])}
                  value={VALUE__ATS__USER__ID}
                  onChange={async (userId) => {
                    await updateState({
                      isValidUser: YES(userId)
                    });
                    onChange(
                      getAtsContextUpdated({
                        entity,
                        atsTypeId: ID__ATS_TYPE__LEVER_API,
                        update: { userId }
                      })
                    );
                  }}
                  options={users}
                />
              </Box>
              <WarningChipMessage show={NOT(VALUE__ATS__USER__ID)}>
                {compileText(EMPLOYER_SETTINGS__LABELS.invalidUser, { CONTACT: userLabel })}
              </WarningChipMessage>
            </Box>
          </Box>
        </Fieldset>
      )}
    </Box>
  );
}
